import { useCallback, useEffect, useRef, useState } from 'react'
import { IDoesFilterPassParams } from 'ag-grid-community'
import { CustomFilterProps, useGridFilter } from 'ag-grid-react'
import { Checkbox, CircularProgress, styled, Typography } from '@mui/material'
import { APIResponse } from '../../../../../../lib/commons/api'
import { intl } from '../../../../../../i18n'
import { BulkSheetFilter, FilterInput } from '../common'
import { Button } from '../../../../../components/buttons'
import {
  SelectFilter,
  SelectFilterFormControl,
  SelectFilterArea,
  CheckboxArea,
  FilterLabel,
} from '../SelectFilter'
import { ICON_SIZE } from '../../../../BulkSheet/const'
import { useSelector } from 'react-redux'
import { AllState } from '../../../../../../store'
import usePrevious from '../../../../../hooks/usePrevious'

type Props = CustomFilterProps & {
  optionKey: string
  fetchOptions?: (params: any) => Promise<object[]>
  getValue: (option: any) => string
  getLabel: (option: any) => string
  fetch: (v: SelectFilter) => Promise<APIResponse>
  getValuesForOptionFilter?: (context: any) => Promise<string>
  sortValues?: (context: any) => any
}

const buildSelectFilterModel = (
  values: string[],
  includeBlank: boolean,
  allOptionValues: string[]
): SelectFilter | null => {
  const isAllSelected =
    allOptionValues.length > 0 &&
    values.length === allOptionValues.length &&
    includeBlank
  return isAllSelected ? null : { values, includeBlank }
}

const ActionBar = styled('div')({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 4,
  margin: '3px 3px 0',
  position: 'relative',
})

const LoadingIndicator = styled(CircularProgress)({
  position: 'absolute',
  left: 0,
})

const ScrollableSelectFilterArea = styled(SelectFilterArea)({
  overflowY: 'auto',
  maxHeight: '500px',
})

const FilterButton = styled(Button)({
  width: '64px',
  height: '20px',
  padding: '2px 6px',
  fontSize: '11px',
})

export const ServerSideSelectFilter = (params: Props) => {
  const {
    model,
    onModelChange,
    api,
    optionKey,
    fetchOptions,
    getValue,
    getLabel,
    fetch: fetchFn,
    getValuesForOptionFilter,
    context,
    sortValues,
  } = params
  const [options, setOptions] = useState<any[]>([])
  const refreshOptions = useCallback(async () => {
    let allOptions = context[optionKey] ?? []
    if (!optionKey && fetchOptions) {
      allOptions = await fetchOptions(params)
    }
    const optionFilterValues =
      getValuesForOptionFilter && (await getValuesForOptionFilter(context))
    const optionsInternal = optionFilterValues
      ? allOptions.filter((option: any) =>
          optionFilterValues.includes(getValue(option))
        )
      : allOptions
    if (sortValues) sortValues(optionsInternal)
    setOptions(optionsInternal)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時と保存後のみ実行。context/params は毎レンダーで新規参照になるため deps に入れない
  }, [])
  // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時のみ実行。refreshOptions は useCallback で安定
  useEffect(() => {
    refreshOptions()
  }, [])
  const hasRequiredSaveData = useSelector<AllState>(
    state => state.hasRequiredSaveData.hasRequiredSaveData
  )
  const prevHasRequiredSaveData = usePrevious(hasRequiredSaveData)

  const allOptionValues = options.map(getValue)

  // Pending model for local UI state (not yet applied to AG Grid)
  const [pendingModel, setPendingModel] = useState<SelectFilter | null>(
    model as SelectFilter | null
  )

  // Sync pending state when model changes (after Apply or external change)
  const modelKey = JSON.stringify(model)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- modelKey は model の値比較用
  useEffect(() => {
    setPendingModel(model as SelectFilter | null)
  }, [modelKey])

  // Derive display values from pending model
  const pendingValues = pendingModel?.values ?? allOptionValues
  const pendingIncludeBlank = pendingModel?.includeBlank ?? true
  const isAllSelected =
    !pendingModel ||
    (pendingValues.length === allOptionValues.length && pendingIncludeBlank)
  const isAllDeselected =
    pendingModel !== null && pendingValues.length === 0 && !pendingIncludeBlank
  const hasPendingChanges = JSON.stringify(pendingModel) !== modelKey

  // UI-local state
  const [text, setText] = useState('')
  const [loading, isLoading] = useState(false)
  const [filteredIds, setFilteredIds] = useState<Set<string> | undefined>(
    undefined
  )

  const addedRowIds = useRef<string[]>()
  useEffect(() => {
    if (addedRowIds.current) return
    addedRowIds.current = []
  }, [])

  const fetchFilteredIds = async (v: SelectFilter) => {
    const response = await fetchFn(v)
    setFilteredIds(new Set(response.json))
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchFilteredIds は毎レンダーで再生成されるが内部で参照する fetchFn は安定
  const search = useCallback(async (v: SelectFilter | null) => {
    if (!v) {
      setFilteredIds(undefined)
      return
    }
    if (v.values.length === 0 && !v.includeBlank) {
      setFilteredIds(new Set())
      return
    }
    try {
      isLoading(true)
      await fetchFilteredIds(v)
    } finally {
      isLoading(false)
    }
  }, [])

  // When model changes (after Apply), trigger search
  // eslint-disable-next-line react-hooks/exhaustive-deps -- modelKey は model の値比較用。model 自体は毎レンダーで新規オブジェクトになるため deps に入れると毎回発火する
  useEffect(() => {
    search(model as SelectFilter | null)
  }, [modelKey])

  // When filteredIds changes, trigger re-filtering
  // eslint-disable-next-line react-hooks/exhaustive-deps -- api は AG Grid から渡される安定参照。filteredIds 変更時のみ再フィルタリングをトリガーする
  useEffect(() => {
    api.onFilterChanged()
  }, [filteredIds])

  // Re-fetch after save
  // eslint-disable-next-line react-hooks/exhaustive-deps -- hasRequiredSaveData の変化のみをトリガーとし、その時点の最新 model/fetchFilteredIds を読む
  useEffect(() => {
    const isAfterSavedOrRefreshed =
      prevHasRequiredSaveData && !hasRequiredSaveData
    isAfterSavedOrRefreshed && refreshOptions()
    if (isAfterSavedOrRefreshed && model) {
      fetchFilteredIds(model as SelectFilter).then(() => {
        addedRowIds.current = []
      })
    }
  }, [hasRequiredSaveData])

  useGridFilter({
    doesFilterPass(params: IDoesFilterPassParams) {
      // Don't filter new rows
      if (params.data.added) {
        if (addedRowIds.current && params.node.id) {
          addedRowIds.current.push(params.node.id)
        }
        return true
      }
      if (
        addedRowIds.current &&
        params.node.id &&
        addedRowIds.current.includes(params.node.id)
      ) {
        return true
      }
      if (!filteredIds) return true
      return params.node.id ? filteredIds.has(params.node.id) : false
    },

    getModelAsString() {
      if (!model) return ''
      const appliedValues = (model as SelectFilter).values
      const appliedIncludeBlank = (model as SelectFilter).includeBlank
      return `${appliedIncludeBlank ? '(Blank),' : ''}${options
        .filter(option => appliedValues.includes(getValue(option)))
        .map(option => getLabel(option))
        .join(',')}`
    },

    afterGuiDetached() {
      if (hasPendingChanges) {
        onModelChange(pendingModel)
      }
      setText('')
    },
  })

  // Helpers for pending model updates
  const updatePendingValues = (newValues: string[]) => {
    setPendingModel(
      buildSelectFilterModel(newValues, pendingIncludeBlank, allOptionValues)
    )
  }

  const updatePendingIncludeBlank = (newIncludeBlank: boolean) => {
    setPendingModel(
      buildSelectFilterModel(pendingValues, newIncludeBlank, allOptionValues)
    )
  }

  const handleApply = () => {
    if (!hasPendingChanges) return
    onModelChange(pendingModel)
  }

  const handleReset = () => {
    setPendingModel(null)
    onModelChange(null)
  }

  return (
    <BulkSheetFilter>
      <FilterInput
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={'Search...'}
        sx={{ height: 22 }}
      />
      <ScrollableSelectFilterArea>
        <SelectFilterFormControl
          label="(Select All)"
          control={
            <Checkbox
              checked={isAllSelected}
              indeterminate={!isAllSelected && !isAllDeselected}
              onChange={e =>
                e.target.checked
                  ? setPendingModel(null)
                  : setPendingModel({ values: [], includeBlank: false })
              }
              disableRipple={true}
            />
          }
        />
        <CheckboxArea>
          {!text && (
            <SelectFilterFormControl
              label="(Blanks)"
              control={
                <Checkbox
                  checked={pendingIncludeBlank}
                  onChange={e => updatePendingIncludeBlank(e.target.checked)}
                  disableRipple={true}
                />
              }
            />
          )}
          {options
            .filter(option => !text || getLabel(option).includes(text))
            .map(option => {
              const value = getValue(option)
              return (
                <SelectFilterFormControl
                  key={`select-filter-${value}`}
                  label={
                    <FilterLabel
                      sx={{
                        backgroundColor: option.backgroundColor ?? 'inherit',
                      }}
                    >
                      {option.iconUrl && (
                        <img
                          src={option.iconUrl}
                          style={{
                            height: `${ICON_SIZE}px`,
                            width: `${ICON_SIZE}px`,
                            borderRadius: '50%',
                          }}
                        />
                      )}
                      <Typography sx={{ padding: '0 3px' }}>
                        {getLabel(option)}
                      </Typography>
                    </FilterLabel>
                  }
                  control={
                    <Checkbox
                      checked={pendingValues.includes(value)}
                      onChange={e => {
                        if (e.target.checked) {
                          updatePendingValues([...pendingValues, value])
                        } else {
                          updatePendingValues(
                            pendingValues.filter(v => v !== value)
                          )
                        }
                      }}
                      disableRipple={true}
                    />
                  }
                />
              )
            })}
        </CheckboxArea>
      </ScrollableSelectFilterArea>
      <ActionBar>
        {loading && <LoadingIndicator size={14} />}
        <FilterButton
          variant="filled"
          colorPattern="skyBlue"
          size="s"
          disabled={!hasPendingChanges}
          onClick={handleApply}
        >
          {intl.formatMessage({ id: 'apply' })}
        </FilterButton>
        <FilterButton
          variant="outlined"
          colorPattern="monotone"
          size="s"
          onClick={handleReset}
        >
          {intl.formatMessage({ id: 'reset' })}
        </FilterButton>
      </ActionBar>
    </BulkSheetFilter>
  )
}
